import { useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { FileText, Layers, Package, Store, UserCheck, Map as MapIcon } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  useConfiguracionFidelizacion,
  useMetadataFidelizacion,
  useMetricasPorCampo,
  useMetricasPorProducto,
  useMetricasPorProvincia,
  useMetricasVentasPorTipo,
  useResumenFidelizacion,
  useSerieTemporal,
  useTodasFacturasFidelizacion,
  useTodosParticipantesFidelizacion,
} from '../hooks/useFidelizacion'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import { normalizeColor, kpiIconClasses } from '../lib/reporteColor'
import { ReporteHeader } from '../components/ReporteHeader'
import { ReporteSubNav } from '../components/ReporteSubNav'
import { usePeriodoReporte } from '../hooks/usePeriodoReporte'
import { ReportePeriodoBar } from '../components/ReportePeriodoBar'
import { StatCardConsolidado } from '../components/StatCardConsolidado'
import { KpiCard } from '../components/KpiCard'
import { TablaFacturasFidelizacion } from '../components/TablaFacturasFidelizacion'
import {
  DynamicFiltersBar,
  type DynamicFiltersValue,
  type FieldOption,
} from '../components/DynamicFiltersBar'
import { mergeCadenasPorNombre } from '../lib/cadenas'
import { autoFieldOptions, participantePairs, parseMulti } from '../lib/campos'
import { useCascadaRepresentante } from '../hooks/useCascadaRepresentante'
import {
  mapaPreciosReporte,
  valorProductoReferencia,
  precioReferenciaPromedioPonderado,
} from '../lib/preciosReferencia'
import { idsProductoDeCategoria, type CategoriaProducto } from '../lib/categoriaProducto'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { BarChartLabelLeft } from '../components/charts/BarChartLabelLeft'
import { BarChartAgrupado, type DatasetOpcion } from '../components/charts/BarChartAgrupado'
import { AreaChartFecha } from '../components/charts/AreaChartFecha'
import { GranularidadToggle } from '../components/GranularidadToggle'
import { agruparSerieFecha, GRAN_RITMO, GRAN_SUFIJO, type Granularidad } from '../lib/serieGranularidad'
import { MapaRegiones } from '../components/charts/MapaRegiones'
import { GEOJSON_PAIS, insetPais, regionTermino, regionTerminoCap, formatMoneda, formatNumero, monedaPais, terminoComprobante } from '../lib/paisMapa'
import { FacturaPreviewDialog } from '../components/FacturaPreviewDialog'
import { parseNumeric } from '../components/charts/palette'

const CAMPO_CADENA_CANDIDATES = ['cadena', 'farmacia', 'codigo_farmacia']
const CAMPO_REPRESENTANTE_CANDIDATES = ['representante', 'rep', 'gerente_zona']
const CAMPO_RAZON_SOCIAL_CANDIDATES = ['razon_social', 'razon-social', 'razonSocial']

function pickCampo(
  campos: Array<{ key: string; agrupable: boolean; label: string }> | undefined,
  candidates: string[],
): { key: string; label: string } | null {
  if (!campos) return null
  for (const cand of candidates) {
    const found = campos.find((c) => c.key === cand && c.agrupable)
    if (found) return { key: found.key, label: found.label }
  }
  return null
}

/**
 * Igual que pickCampo pero usando `filtrable` en vez de `agrupable`.
 * Sirve para localizar el campo a usar como llave en `fieldOptions` del filtro.
 */
function pickCampoFiltrable(
  campos: Array<{ key: string; filtrable: boolean; tipo: string; label: string }> | undefined,
  candidates: string[],
): { key: string; label: string } | null {
  if (!campos) return null
  // No se filtra por `tipo` porque algunos upstream devuelven literales no
  // estándar (ej. 'texto'/'str') que igual representan strings.
  for (const cand of candidates) {
    const found = campos.find((c) => c.key === cand && c.filtrable)
    if (found) return { key: found.key, label: found.label }
  }
  return null
}

function formatDecimal(value: number | string | null | undefined, configId: number, decimals = 2): string {
  if (value === null || value === undefined) return '—'
  const num = typeof value === 'string' ? parseFloat(value) : value
  if (Number.isNaN(num)) return '—'
  return formatNumero(num, configId, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

export function VentasFidelizacionPage() {
  const { id } = useParams<{ id: string }>()
  const configId = id ? parseInt(id, 10) : 0

  const config = useConfiguracionFidelizacion(configId)
  const metadata = useMetadataFidelizacion(configId)

  // Modo de acumulación del programa. En 'valor' las métricas de volumen se
  // expresan en $; en 'cantidad' en cajas equivalentes. La tab de la sección
  // permite alternar manualmente entre "cajas" y "$" (sobreescribe el default).
  const puntoModoValor = (metadata.data?.puntos_modo ?? 'cantidad') === 'valor'
  // Algunos reportes valoran las ventas en $ con una lista de precios local
  // (precio_lista × cajas) en vez del monto del backend: Medifarma (por
  // slug/nombre) y Grupo Farma Ecuador (id 3). `mapaPrecios` es el mapa de esos
  // precios, o null si el reporte usa el monto del backend directamente.
  const mapaPrecios = useMemo(
    () =>
      mapaPreciosReporte({
        id: configId,
        slug: config.data?.slug,
        nombre: config.data?.nombre,
      }),
    [configId, config.data?.slug, config.data?.nombre],
  )
  const usaPreciosLocales = mapaPrecios != null
  // El export "Ventas por representante" (con meta de $300) es una regla de
  // negocio exclusiva de Sanfer; sólo se ofrece en ese reporte.
  const esSanfer = /sanfer/i.test(`${config.data?.slug ?? ''} ${config.data?.nombre ?? ''}`)
  // La tab Cajas/$ (alternar entre "unidades vendidas" y "total vendido") se
  // habilita para los reportes con lista de precios local: Medifarma Ecuador
  // (id 5, $), Grupo Farma Ecuador (id 3) y Medifarma Perú (id 7, S/). El resto
  // queda fijo en "unidades vendidas".
  const puedeAlternarVista = configId === 5 || configId === 3 || configId === 7
  // null = usar el default del programa; se sobreescribe al tocar la tab.
  const [vistaOverride, setVistaOverride] = useState<'cajas' | 'dinero' | null>(null)
  const vista: 'cajas' | 'dinero' = (puedeAlternarVista && vistaOverride)
    ? vistaOverride
    : (puntoModoValor ? 'dinero' : 'cajas')
  const esValor = vista === 'dinero'
  // Granularidad de la gráfica de ventas por fecha (día / semana / mes). En las
  // vistas agregadas (semana/mes) se amplía el rango a todo el histórico para que
  // haya suficientes periodos (ver `filtrosSerie`).
  const [granSerie, setGranSerie] = useState<Granularidad>('dia')
  const esAmpliado = granSerie !== 'dia'
  // Símbolo de moneda del reporte (S/ en Perú, $ en el resto) para los títulos.
  const simboloMoneda = monedaPais(configId).simbolo

  const formatVolumen = (v: number | string | null | undefined) =>
    esValor ? formatMoneda(v, configId) : formatDecimal(v, configId, 2)

  const ventasCardEsValor = esValor
  const formatVolumenCard = (v: number | string | null | undefined) =>
    ventasCardEsValor ? formatMoneda(v, configId) : formatDecimal(v, configId, 2)

  // $ de un producto: en reportes con lista de precios local se valora con
  // precio de lista × cajas equivalentes; sin precio de lista (o en reportes sin
  // lista) se usa el monto del backend como respaldo.
  const valorProductoDinero = useCallback(
    (p: { nombre: string; monto?: unknown; cantidad_vendida?: unknown }) =>
      mapaPrecios ? valorProductoReferencia(mapaPrecios, p) : parseNumeric(p.monto),
    [mapaPrecios],
  )

  // "Todo el histórico": al activarse se omite el rango de fechas y todas las
  // métricas se calculan sobre el total de datos del programa. `modo` alterna
  // entre selector de mes y rango de fechas personalizado.
  const {
    mes,
    setMes,
    sinFecha,
    setSinFecha,
    modo,
    setModo,
    rangoPersonalizado,
    setRangoPersonalizado,
    rango,
    rangoEfectivo,
  } = usePeriodoReporte()

  const [facturaId, setFacturaId] = useState<number | string | null>(null)
  const [facturaOpen, setFacturaOpen] = useState(false)
  const [filtrosFacturas, setFiltrosFacturas] = useState<DynamicFiltersValue>({})

  // Datos base para los filtros cascada (Jefe de Ventas / Representante Médico).
  // Se cargan aquí porque el conjunto de representantes del jefe se inyecta en
  // `filtrosCompletos`, que alimenta todas las métricas server-side de abajo.
  const todosParticipantes = useTodosParticipantesFidelizacion(configId)
  const camposExternos = metadata.data?.campos_externos
  const campoRepFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )
  const { cascadeSelects, inyectarReps, repSetCliente, narrowRepOptions } = useCascadaRepresentante({
    configId,
    participantes: todosParticipantes.data,
    repKey: campoRepFiltro?.key,
    filtros: filtrosFacturas,
    setFiltros: setFiltrosFacturas,
  })
  const filtrosInyectados = useMemo(
    () => inyectarReps(filtrosFacturas),
    [filtrosFacturas, inyectarReps],
  )

  // Filtro "Razón Social": el campo vive en el participante
  // (`datos_extra.razon_social`), NO en la factura — el endpoint de facturas del
  // upstream no lo conoce, así que enviarlo como filtro devuelve 0 registros. Se
  // traduce al conjunto de `participante_id` cuya razón social coincide y se
  // filtra client-side sobre el listado ya cargado, igual que la cascada de
  // representante con `repSetCliente`.
  const campoRazonFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_RAZON_SOCIAL_CANDIDATES),
    [camposExternos],
  )
  const razonSel = campoRazonFiltro ? filtrosFacturas[campoRazonFiltro.key] : undefined
  const razonSet = useMemo(() => {
    if (!campoRazonFiltro) return null
    const vals = new Set(parseMulti(razonSel))
    if (vals.size === 0) return null
    const k = campoRazonFiltro.key
    const ids = new Set<string>()
    for (const p of todosParticipantes.data ?? []) {
      const de = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
      const val = de[k]
      if (val != null && vals.has(String(val))) ids.add(String(p.id))
    }
    return ids
  }, [campoRazonFiltro, razonSel, todosParticipantes.data])
  const razonClienteActiva = razonSet != null

  // Filtros globales aplicados a TODO el reporte (cards, charts, tabla).
  // `filtrosCompletos` = rango + filtros (provincia, cadena, rep, etc.), con el
  // conjunto de representantes del jefe inyectado si hay cascada activa.
  //
  // `producto` se aplica SOLO client-side (`filtrarPorProducto`): el upstream no
  // filtra las métricas agregadas por producto (ver §4.3). No se envía al backend
  // porque con varios productos seleccionados genera un parámetro repetido
  // (`producto=1&producto=2`) que rompe la respuesta del upstream y deja cards,
  // barras y serie sin datos. Se conserva en `filtrosFacturas`/`productoSel`.
  const filtrosCompletos = useMemo(() => {
    const enviar: DynamicFiltersValue = { ...rangoEfectivo, ...filtrosInyectados }
    delete enviar.producto
    delete enviar.categoria
    // La razón social no se envía al upstream (no es un filtro de facturas); se
    // aplica client-side vía `razonSet`. Todas las superficies se derivan del
    // listado cuando está activa, así que quitarla evita respuestas en cero.
    if (campoRazonFiltro) delete enviar[campoRazonFiltro.key]
    return enviar
  }, [rangoEfectivo, filtrosInyectados, campoRazonFiltro])

  const filtrosTabla = useMemo(
    () => ({ ...filtrosCompletos, solo_validadas: true, incluir_duplicadas: true }),
    [filtrosCompletos],
  )

  // Lista completa de productos del periodo (SIN filtros dinámicos) — puebla el
  // select de Producto y resuelve la categoría Consumo/Farma (por nombre) a ids.
  const porProductoCatalogo = useMetricasPorProducto(configId, rangoEfectivo)

  // Categoría Consumo/Farma (solo reporte 7): se resuelve a un conjunto de
  // producto_id por coincidencia NO exacta de nombre contra `consumo-farma.json`.
  const categoriaSel = filtrosFacturas.categoria as CategoriaProducto | undefined
  const idsCategoria = useMemo(
    () =>
      categoriaSel
        ? idsProductoDeCategoria(porProductoCatalogo.data?.datos ?? [], categoriaSel)
        : null,
    [categoriaSel, porProductoCatalogo.data],
  )

  // Producto seleccionado en el filtro. El upstream NO filtra las métricas
  // agregadas por producto (solo desde/hasta/provincia, ver spec §4.3), así que
  // cuando hay un producto elegido lo aplicamos client-side sobre los datos de
  // `por-producto` (única fuente con una fila por producto) y atenuamos las
  // secciones sin desglose por producto.
  //
  // Selección EFECTIVA = filtro explícito ∩ categoría (si hay). Todo lo demás de
  // la página deriva de aquí, así que la categoría se comporta como una
  // multi-selección de productos. Centinela si la categoría no tiene coincidencias.
  const productoSel = useMemo(() => {
    const explicito = parseMulti(filtrosFacturas.producto)
    if (!idsCategoria) return filtrosFacturas.producto
    const combinado =
      explicito.length > 0 ? explicito.filter((id) => idsCategoria.has(id)) : [...idsCategoria]
    return combinado.length > 0 ? combinado.join(',') : '__sin_categoria__'
  }, [filtrosFacturas.producto, idsCategoria])
  // Producto es multi-selección (coma-separado): una fila pasa si su id está en el
  // conjunto elegido. Sin selección, no filtra.
  const productosSelSet = useMemo(() => new Set(parseMulti(productoSel)), [productoSel])
  const filtrarPorProducto = useCallback(
    <T extends { producto_id: number },>(datos: T[]): T[] =>
      productosSelSet.size > 0
        ? datos.filter((p) => productosSelSet.has(String(p.producto_id)))
        : datos,
    [productosSelSet],
  )

  const resumen = useResumenFidelizacion(configId, filtrosCompletos)
  const porProvincia = useMetricasPorProvincia(configId, filtrosCompletos)
  // Todos los productos del periodo (sin límite) — alimenta el KPI "Unidades
  // Vendidas en Caja", el badge de productos únicos y la gráfica "Top productos".
  const porProductoTotal = useMetricasPorProducto(configId, filtrosCompletos)
  // Mix caja/unidad/desconocido por producto. Única fuente con conteo de
  // comprobantes por producto: sirve para reflejar el filtro de producto en la
  // card de comprobantes (el upstream no filtra /metricas/resumen por producto).
  // Solo se consulta cuando hay un producto seleccionado (configId 0 = deshabilitado).
  // Usa `filtrosCompletos` (no solo el rango) para que la card de comprobantes
  // también respete provincia/ciudad/representante (el upstream los honra aquí).
  const ventasPorTipo = useMetricasVentasPorTipo(productoSel ? configId : 0, {
    ...filtrosCompletos,
    top: 100,
  })

  const campoCadena = useMemo(() => pickCampo(camposExternos, CAMPO_CADENA_CANDIDATES), [camposExternos])
  const campoRepresentante = useMemo(
    () => pickCampo(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )

  const porCadena = useMetricasPorCampo(configId, campoCadena?.key ?? null, { ...filtrosCompletos, top: 10 })
  const porRep = useMetricasPorCampo(configId, campoRepresentante?.key ?? null, { ...filtrosCompletos, top: 10 })

  // Campos a usar para poblar los dropdowns de los filtros de la tabla de
  // Registros: se buscan en `filtrable` (no en `agrupable`) porque el dropdown
  // se renderiza junto al input filtrable existente, que es independiente de
  // si la métrica de agrupación está disponible.
  const campoCadenaFiltro = useMemo(
    () => pickCampoFiltrable(camposExternos, CAMPO_CADENA_CANDIDATES),
    [camposExternos],
  )

  // Se agregan las facturas client-side (con sus líneas de producto) en dos casos:
  //  1) Producto seleccionado: el upstream no ofrece desglose por producto de las
  //     agregaciones por provincia/cadena/representante ni de la serie diaria.
  //  2) Cascada Jefe de Ventas / Supervisor activa: el upstream NO soporta filtrar
  //     las agregaciones (/metricas/*) por varios representantes (solo honra el
  //     último), así que derivamos todo del listado (que sí filtra por el conjunto).
  const cascadaClienteActiva = !!repSetCliente
  // Superficies derivadas client-side: cascada de representante O filtro de
  // razón social (ambos filtran el listado por un atributo del participante que
  // el upstream no honra en las agregaciones/facturas).
  const derivadoCliente = cascadaClienteActiva || razonClienteActiva
  const todasFacturas = useTodasFacturasFidelizacion(
    configId,
    { ...rangoEfectivo, solo_validadas: true },
    !!productoSel || derivadoCliente,
  )

  // Serie diaria del volumen de ventas en el mes. En reportes con lista de
  // precios local siempre se pide en cajas equivalentes y el $ se obtiene
  // multiplicando por el precio de referencia promedio ponderado (así el total
  // de la serie coincide con la valoración por producto). En el resto: $ (monto)
  // o cajas según modo.
  // En vista mensual se amplía el rango a todo el histórico (se omiten
  // desde/hasta) conservando el resto de filtros dinámicos; en diario se respeta
  // el periodo seleccionado. Solo afecta a la gráfica de ventas por fecha.
  const filtrosSerie = useMemo(() => {
    if (!esAmpliado) return filtrosCompletos
    const resto: DynamicFiltersValue = { ...filtrosCompletos }
    delete resto.desde
    delete resto.hasta
    return resto
  }, [esAmpliado, filtrosCompletos])

  const serie = useSerieTemporal(configId, {
    granularidad: granSerie,
    metrica: usaPreciosLocales ? 'cajas_eq' : esValor ? 'monto' : 'cajas_eq',
    ...filtrosSerie,
  })
  // Segunda serie: nº de facturas validadas por periodo (línea gris en el chart).
  const serieFacturas = useSerieTemporal(configId, {
    granularidad: granSerie,
    metrica: 'facturas',
    ...filtrosSerie,
  })

  const color = normalizeColor(config.data)
  const kpiColor = kpiIconClasses(color.nombre, color.tono)
  const errorGlobal = config.error || metadata.error || resumen.error

  const handleRefresh = () => {
    metadata.refetch()
    resumen.refetch()
    porProvincia.refetch()
    porProductoTotal.refetch()
    ventasPorTipo.refetch()
    todasFacturas.refetch()
    porCadena.refetch()
    porRep.refetch()
    serie.refetch()
    serieFacturas.refetch()
  }

  const refreshing =
    metadata.isFetching ||
    resumen.isFetching ||
    porProvincia.isFetching ||
    porProductoTotal.isFetching ||
    ventasPorTipo.isFetching ||
    todasFacturas.isFetching ||
    porCadena.isFetching ||
    porRep.isFetching ||
    serie.isFetching ||
    serieFacturas.isFetching

  // Filas de por-producto ya filtradas al producto seleccionado (si lo hay).
  const datosProductoTotal = useMemo(
    () => filtrarPorProducto(porProductoTotal.data?.datos ?? []),
    [porProductoTotal.data, filtrarPorProducto],
  )

  // Volumen total del periodo: $ vendido en modo valor, cajas equivalentes en
  // modo cantidad. Se limita al producto seleccionado si hay filtro activo.
  // (server-side; bajo cascada se recalcula abajo desde el listado.)
  const totalVolumenServer = useMemo(() => {
    return datosProductoTotal.reduce(
      (s, p) =>
        s + (ventasCardEsValor ? valorProductoDinero(p) : parseNumeric(p.cantidad_vendida)),
      0,
    )
  }, [datosProductoTotal, ventasCardEsValor, valorProductoDinero])

  // Comprobantes validados del periodo (todos los productos), desde /resumen.
  // (server-side; bajo cascada se recalcula abajo desde el listado.)
  const validadasServer = resumen.data?.facturas.validadas ?? 0
  const duplicadas = resumen.data?.facturas.duplicadas ?? 0
  // Con producto seleccionado, /resumen no filtra por producto: el conteo de
  // comprobantes del producto se deriva de ventas-por-tipo (facturas por bucket
  // de tipo de venta). Un comprobante cae en un solo bucket, así que sumarlos da
  // el nº de comprobantes que incluyen ese producto.
  const validadasProducto = useMemo(
    () =>
      filtrarPorProducto(ventasPorTipo.data?.datos ?? []).reduce(
        (s, r) =>
          s + r.facturas_caja + r.facturas_unidad + r.facturas_desconocido + (r.facturas_valor ?? 0),
        0,
      ),
    [ventasPorTipo.data, filtrarPorProducto],
  )
  // Nombre del producto solo cuando hay exactamente uno elegido; con varios, el
  // banner muestra el conteo en su lugar.
  const productoNombreSel =
    productosSelSet.size === 1 ? (datosProductoTotal[0]?.nombre ?? null) : null

  // Agregación client-side del producto seleccionado por provincia, cadena,
  // representante y día, uniendo cada línea de factura del producto con la
  // provincia/cadena/rep de su participante. El valor de cada línea se calcula
  // con `valorProductoDinero` (precios locales en $ / cajas en modo cantidad),
  // igual que la card y el Top productos, para que todos los cortes cuadren.
  const agregadosProducto = useMemo(() => {
    // Ruta client-side activa con producto seleccionado, cascada
    // (jefe/supervisor) activa o filtro de razón social; ver `todasFacturas`.
    if (!productoSel && !derivadoCliente) return null
    const partMap = new Map<string, Record<string, unknown>>()
    for (const p of todosParticipantes.data ?? []) partMap.set(String(p.id), p)

    const cadenaKey = campoCadenaFiltro?.key
    const repKey = campoRepFiltro?.key
    const SIN = '(sin asignar)'
    const provincia = new Map<string, number>()
    const cadena = new Map<string, number>()
    const rep = new Map<string, number>()
    const dia = new Map<string, number>()
    const diaFacturas = new Map<string, number>()
    // Desglose por producto (para Top productos y KPI de volumen bajo cascada):
    // acumula valor ($) y cantidad por separado para servir ambos modos.
    const producto = new Map<string, { nombre: string; valor: number; cantidad: number }>()
    // Facturas (comprobantes completos) que pasan el filtro de producto/cascada:
    // alimentan la tabla de comprobantes client-side cuando hay producto activo.
    const facturas: Array<Record<string, unknown>> = []
    let comprobantes = 0

    for (const f of todasFacturas.data ?? []) {
      // Cascada Jefe de Ventas / Supervisor: restringe las facturas a los
      // participantes cuyo representante pertenece al jefe/supervisor elegido.
      if (repSetCliente) {
        const de0 = (partMap.get(String(f.participante_id))?.datos_extra as Record<string, unknown>) ?? {}
        if (!repKey || !repSetCliente.has(String(de0[repKey] ?? ''))) continue
      }
      // Filtro Razón Social: sólo facturas de participantes cuya razón social
      // coincide con la selección (traducida a un conjunto de participante_id).
      if (razonSet && !razonSet.has(String(f.participante_id))) continue
      // Con producto elegido, solo sus líneas; sin producto (cascada), todas las
      // líneas de participante de la factura.
      const lineas = ((f.productos as Array<Record<string, unknown>>) ?? []).filter(
        (l) =>
          l.es_participante &&
          (productosSelSet.size === 0 || productosSelSet.has(String(l.producto_id))),
      )
      if (lineas.length === 0) continue
      comprobantes += 1
      facturas.push(f)
      let val = 0
      for (const l of lineas) {
        const lineaValor = valorProductoDinero({
          nombre: String(l.nombre ?? ''),
          monto: l.precio_total,
          cantidad_vendida: l.cantidad,
        })
        const lineaCant = parseNumeric(l.cantidad)
        val += esValor ? lineaValor : lineaCant
        const pid = String(l.producto_id ?? '')
        const prev = producto.get(pid)
        if (prev) {
          prev.valor += lineaValor
          prev.cantidad += lineaCant
        } else {
          producto.set(pid, { nombre: String(l.nombre ?? ''), valor: lineaValor, cantidad: lineaCant })
        }
      }
      const part = partMap.get(String(f.participante_id))
      const de = (part?.datos_extra as Record<string, unknown>) ?? {}
      const provNombre = (part?.provincia_nombre as string)?.trim() || SIN
      provincia.set(provNombre, (provincia.get(provNombre) ?? 0) + val)
      if (cadenaKey) {
        const c = (de[`${cadenaKey}_nombre`] as string)?.trim() || SIN
        cadena.set(c, (cadena.get(c) ?? 0) + val)
      }
      if (repKey) {
        const r = (de[`${repKey}_nombre`] as string)?.trim() || SIN
        rep.set(r, (rep.get(r) ?? 0) + val)
      }
      const fecha = String(f.fecha_carga ?? f.fecha_emision ?? '').slice(0, 10)
      if (fecha) {
        dia.set(fecha, (dia.get(fecha) ?? 0) + val)
        diaFacturas.set(fecha, (diaFacturas.get(fecha) ?? 0) + 1)
      }
    }

    const toArr = (m: Map<string, number>) =>
      Array.from(m, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value)
    const fechas = Array.from(new Set([...dia.keys(), ...diaFacturas.keys()])).sort()
    return {
      provincia: toArr(provincia),
      cadena: mergeCadenasPorNombre(toArr(cadena)),
      rep: toArr(rep),
      fecha: fechas.map((f) => ({
        fecha: f,
        total: dia.get(f) ?? 0,
        secondary: diaFacturas.get(f) ?? 0,
      })),
      producto: Array.from(producto, ([producto_id, v]) => ({
        producto_id,
        nombre: v.nombre,
        valor: v.valor,
        cantidad: v.cantidad,
      })),
      facturas,
      comprobantes,
    }
  }, [
    productoSel,
    productosSelSet,
    derivadoCliente,
    todasFacturas.data,
    todosParticipantes.data,
    campoCadenaFiltro?.key,
    campoRepFiltro?.key,
    esValor,
    valorProductoDinero,
    repSetCliente,
    razonSet,
  ])

  // Bajo cascada o razón social sin producto puntual, las superficies
  // por-producto (Top productos, volumen, comprobantes) también se derivan
  // client-side.
  const derivadoSinProducto = derivadoCliente && !productoSel

  const totalVolumen =
    derivadoSinProducto && agregadosProducto
      ? agregadosProducto.producto.reduce(
          (s, p) => s + (ventasCardEsValor ? p.valor : p.cantidad),
          0,
        )
      : totalVolumenServer
  const validadas =
    derivadoSinProducto && agregadosProducto ? agregadosProducto.comprobantes : validadasServer

  // En modo valor se grafica el monto ($); en modo cantidad, las cajas eq.
  const cadenaData = useMemo(
    () =>
      agregadosProducto
        ? agregadosProducto.cadena
        : mergeCadenasPorNombre(
            (porCadena.data?.datos ?? []).map((d) => ({
              label: d.valor,
              value: parseNumeric(esValor ? d.monto : d.cantidad_cajas_eq),
            })),
          ),
    [agregadosProducto, porCadena.data, esValor],
  )
  const repData = useMemo(
    () =>
      agregadosProducto
        ? agregadosProducto.rep
        : (porRep.data?.datos ?? []).map((d) => ({
            label: d.valor,
            value: parseNumeric(esValor ? d.monto : d.cantidad_cajas_eq),
          })),
    [agregadosProducto, porRep.data, esValor],
  )
  const provinciaData = useMemo(
    () =>
      agregadosProducto
        ? agregadosProducto.provincia
        : (porProvincia.data?.datos ?? []).map((d) => ({
            label: d.valor,
            value: parseNumeric(esValor ? d.monto : d.cantidad_cajas_eq),
          })),
    [agregadosProducto, porProvincia.data, esValor],
  )
  const productoData = useMemo(
    () =>
      derivadoSinProducto && agregadosProducto
        ? agregadosProducto.producto
            .map((p) => ({ label: p.nombre, value: esValor ? p.valor : p.cantidad }))
            .sort((a, b) => b.value - a.value)
        : filtrarPorProducto(porProductoTotal.data?.datos ?? [])
            .map((p) => ({
              label: p.nombre,
              value: esValor ? valorProductoDinero(p) : parseNumeric(p.cantidad_vendida),
            }))
            .sort((a, b) => b.value - a.value),
    [derivadoSinProducto, agregadosProducto, porProductoTotal.data, esValor, valorProductoDinero, filtrarPorProducto],
  )

  const fieldOptionsFiltros = useMemo(() => {
    const out: Record<string, FieldOption[]> = {}
    const participantes = todosParticipantes.data ?? []

    const collectPairs = (
      getId: (p: Record<string, unknown>) => unknown,
      getNombre: (p: Record<string, unknown>) => unknown,
      dedupePorNombre = false,
    ): FieldOption[] => {
      // El upstream a veces devuelve varios IDs y/o variantes de formato
      // (espacios sobrantes, mayúsculas, acentos) para una misma cadena, lo que
      // generaba opciones duplicadas en el filtro. Estandarizamos el nombre;
      // con `dedupePorNombre` (sólo cadena) deduplicamos por nombre normalizado
      // y conservamos un ID representativo estable (el menor). En representante
      // NO se dedupe por nombre: dos personas distintas pueden llamarse igual.
      const limpiar = (s: string) => s.trim().replace(/\s+/g, ' ')
      const clave = (s: string) =>
        limpiar(s).toLocaleLowerCase('es').normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const esMenor = (a: string, b: string) => {
        const na = Number(a)
        const nb = Number(b)
        if (!Number.isNaN(na) && !Number.isNaN(nb)) return na < nb
        return a.localeCompare(b) < 0
      }

      const seen = new Map<string, { value: string; label: string }>()
      for (const p of participantes) {
        const id = getId(p)
        if (id === undefined || id === null || String(id).trim() === '') continue
        const value = String(id).trim()
        const nombre = getNombre(p)
        const label = nombre ? limpiar(String(nombre)) : value
        const key = dedupePorNombre ? clave(label) : value
        const prev = seen.get(key)
        if (!prev) seen.set(key, { value, label })
        else if (dedupePorNombre && esMenor(value, prev.value)) {
          seen.set(key, { value, label: prev.label })
        }
      }
      return Array.from(seen.values()).sort((a, b) =>
        a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }),
      )
    }

    const provinciaPairs = collectPairs(
      (p) => p.provincia,
      (p) => p.provincia_nombre,
    )
    if (provinciaPairs.length > 0) out.provincia = provinciaPairs

    if (campoCadenaFiltro?.key) {
      const k = campoCadenaFiltro.key
      out[k] = collectPairs(
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[k],
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[`${k}_nombre`],
        true,
      )
    }
    if (campoRepFiltro?.key) {
      const k = campoRepFiltro.key
      out[k] = collectPairs(
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[k],
        (p) => (p.datos_extra as Record<string, unknown> | undefined)?.[`${k}_nombre`],
      )
    }

    // Productos: catálogo completo del periodo (sin filtros dinámicos) — así
    // el select de Producto siempre muestra todas las opciones disponibles.
    const productos = porProductoCatalogo.data?.datos ?? []
    if (productos.length > 0) {
      out.producto = productos
        .map((p) => ({ value: String(p.producto_id), label: p.nombre }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
    }

    // Resto de campos de texto filtrables (ej. Razón social): dropdown con los
    // valores reales del listado en vez de un input de texto libre.
    const cubiertos = [
      'provincia',
      'producto',
      ...(campoCadenaFiltro?.key ? [campoCadenaFiltro.key] : []),
      ...(campoRepFiltro?.key ? [campoRepFiltro.key] : []),
    ]
    for (const [k, opts] of Object.entries(
      autoFieldOptions(participantes, camposExternos, cubiertos),
    )) {
      if (!out[k]) out[k] = opts
    }

    // Dependiente (reporte 7): dropdown de participantes registrados por nombre.
    if (configId === 7) {
      const pares = participantePairs(participantes)
      if (pares.length > 0) out.participante_id = pares
    }

    return out
  }, [todosParticipantes.data, campoCadenaFiltro?.key, campoRepFiltro?.key, porProductoCatalogo.data, camposExternos, configId])


  // Precio de referencia promedio ponderado del periodo (sólo reportes con lista
  // de precios local), para valorar en $ la serie diaria de cajas.
  const precioRefPromedio = useMemo(
    () =>
      mapaPrecios
        ? precioReferenciaPromedioPonderado(mapaPrecios, datosProductoTotal)
        : null,
    [mapaPrecios, datosProductoTotal],
  )

  const fechaData = useMemo(() => {
    // Con producto seleccionado, la serie se agrega desde las facturas del
    // producto (misma fuente que el resto de cortes por producto). En vista
    // mensual se reagrupa client-side por mes (el path server-side ya viene
    // agregado por el backend).
    if (agregadosProducto) return agruparSerieFecha(agregadosProducto.fecha, granSerie)
    // En $ (reportes con lista de precios) la serie viene en cajas; se valora al
    // precio de referencia promedio. En otro caso el factor es 1 (serie ya en su
    // unidad).
    const factor = esValor && precioRefPromedio != null ? precioRefPromedio : 1
    const volMap = new Map(
      (serie.data?.serie ?? []).map((p) => [p.periodo, parseNumeric(p.valor) * factor]),
    )
    const facMap = new Map(
      (serieFacturas.data?.serie ?? []).map((p) => [p.periodo, parseNumeric(p.valor)]),
    )
    const fechas = Array.from(new Set([...volMap.keys(), ...facMap.keys()])).sort()
    return fechas.map((f) => ({
      fecha: f,
      total: volMap.get(f) ?? 0,
      secondary: facMap.get(f) ?? 0,
    }))
  }, [agregadosProducto, granSerie, serie.data, serieFacturas.data, esValor, precioRefPromedio])

  // Promedios diarios del periodo: ventas ($/cajas) y nº de facturas por día.
  // Se promedia solo sobre los días con comprobantes (secondary > 0), no sobre
  // el mes calendario: la serie de volumen puede venir con días en cero desde el
  // upstream, lo que infla el divisor y baja artificialmente los promedios.
  const promedioDiario = useMemo(() => {
    const diasConFacturas = fechaData.filter((d) => d.secondary > 0)
    const dias = diasConFacturas.length
    if (dias === 0) return { ventas: 0, facturas: 0 }
    const sumVentas = diasConFacturas.reduce((s, d) => s + d.total, 0)
    const sumFacturas = diasConFacturas.reduce((s, d) => s + d.secondary, 0)
    return { ventas: sumVentas / dias, facturas: sumFacturas / dias }
  }, [fechaData])

  // Participantes filtrados client-side para conteos únicos del header que
// Conteos únicos derivados del response del API (ya filtrado por fecha y filtros activos).
  // Cuenta valores con dato > 0, excluyendo "(sin asignar)". Con producto
  // seleccionado usa los agregados por producto; si no, el response del API.
  const contarUnicos = (
    filas: Array<{ label?: string; valor?: string; value?: number; cantidad_cajas_eq?: unknown }>,
    usarValue: boolean,
  ) =>
    filas.filter((d) => {
      const label = usarValue ? d.label : d.valor
      const v = usarValue ? Number(d.value ?? 0) : Number(d.cantidad_cajas_eq ?? 0)
      return label !== '(sin asignar)' && parseFloat(v.toFixed(2)) > 0
    }).length
  // Con producto seleccionado, provincia/cadena/rep/serie se agregan desde las
  // facturas; su estado de carga depende de facturas + participantes.
  const cargandoAgregado =
    (!!productoSel || derivadoCliente) && (todasFacturas.isLoading || todosParticipantes.isLoading)
  // Facturas para la tabla de comprobantes cuando hay producto seleccionado: el
  // upstream no filtra el listado de facturas por producto, así que la tabla se
  // alimenta client-side con los comprobantes que incluyen el/los producto(s)
  // (mismo conjunto que usan las cards y charts). `null` = modo server-side normal.
  const datosTablaProducto = useMemo(
    () => (productoSel || razonClienteActiva ? (agregadosProducto?.facturas ?? []) : null),
    [productoSel, razonClienteActiva, agregadosProducto],
  )
  const totalProductos =
    derivadoSinProducto && agregadosProducto ? agregadosProducto.producto.length : datosProductoTotal.length
  const totalCadenas = agregadosProducto
    ? contarUnicos(agregadosProducto.cadena, true)
    : contarUnicos(porCadena.data?.datos ?? [], false)
  const totalRepresentantes = agregadosProducto
    ? contarUnicos(agregadosProducto.rep, true)
    : contarUnicos(porRep.data?.datos ?? [], false)
  const totalProvincias = agregadosProducto
    ? contarUnicos(agregadosProducto.provincia, true)
    : contarUnicos(porProvincia.data?.datos ?? [], false)

  return (
    <div className="space-y-6">
      <ReporteHeader
        config={config.data}
        programaNombre={metadata.data?.programa.nombre}
        icon={FileText}
        seccion="Ventas"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <ReporteSubNav
        configId={configId}
        colorNombre={color.nombre}
        colorTono={color.tono}
      />

      <ReportePeriodoBar
        rango={rango}
        mes={mes}
        onMesChange={setMes}
        minDate={metadata.data?.rango_datos?.primera_factura ?? null}
        maxDate={metadata.data?.rango_datos?.ultima_factura ?? null}
        configId={configId}
        colorNombre={color.nombre}
        exportTipos={
          // Medifarma Perú (id 7): un solo Excel con facturas + detalle por
          // medicamento en dos hojas (evita descargar dos archivos separados).
          configId === 7
            ? [{ tipo: 'ventas-completo' as const, label: `Ventas (${terminoComprobante(configId, { plural: true })} + medicamentos)` }]
            : [
                { tipo: 'facturas', label: terminoComprobante(configId, { plural: true, capitalize: true }) },
                { tipo: 'medicamentos', label: 'Medicamentos (detalle)' },
                // Registros y ventas (solo Sanfer): XLSX de dos hojas — registros por
                // representante (meta 100) y ventas por dependiente (meta $300).
                // Lleva el rango del periodo activo como filtro propio para que las
                // metas se calculen sobre el mes seleccionado (o todo el histórico).
                ...(esSanfer
                  ? [{
                      tipo: 'ventas-representantes' as const,
                      label: 'Registros y ventas',
                      filtros: rangoEfectivo,
                    }]
                  : []),
              ]
        }
        // Mismas facturas que la tabla: validadas + duplicadas (4 estados).
        exportFiltros={{ ...filtrosInyectados, solo_validadas: true, incluir_duplicadas: true }}
        sinFecha={sinFecha}
        onSinFechaChange={setSinFecha}
        modo={modo}
        onModoChange={setModo}
        rangoPersonalizado={rangoPersonalizado}
        onRangoPersonalizadoChange={setRangoPersonalizado}
      />

      {/* Filtros globales del reporte: provincia / producto / cadena / representante.
          Aplican a cards, charts y tabla. */}
      <DynamicFiltersBar
        configId={configId}
        metadata={metadata.data}
        value={filtrosFacturas}
        onChange={setFiltrosFacturas}
        hideDateRange
        showCategoria={configId === 7}
        omitDynamicFields={['provincia', 'nivel', 'email_verificado', 'activo']}
        fieldOptions={narrowRepOptions(fieldOptionsFiltros)}
        cascadeSelects={cascadeSelects}
      />

      {errorGlobal && (
        <Alert variant="destructive">
          <AlertDescription>{parseFidelizacionError(errorGlobal).message}</AlertDescription>
        </Alert>
      )}

      {productoSel && (
        <Alert>
          <AlertDescription>
            Filtrando por {productosSelSet.size > 1 ? `${productosSelSet.size} productos` : 'producto'}
            {productoNombreSel ? ` "${productoNombreSel}"` : ''}. Las tarjetas,
            el Top productos y las secciones por {regionTermino(configId)}, cadena, representante y la
            serie diaria reflejan solo {productosSelSet.size > 1 ? 'estos productos' : 'este producto'}.
            La tabla de comprobantes muestra solo los comprobantes que incluyen
            {productosSelSet.size > 1 ? ' alguno de estos productos' : ' este producto'} (el monto y
            los puntos de cada comprobante son los totales del comprobante completo).
          </AlertDescription>
        </Alert>
      )}

      {/* Tab de visualización (reportes con lista de precios local, id 5 y 3):
          cajas vendidas vs total vendido en $. El $ se calcula con precios de
          referencia × cajas por producto. */}
      {puedeAlternarVista && (
        <Tabs value={vista} onValueChange={(v) => setVistaOverride(v as 'cajas' | 'dinero')}>
          <TabsList className="grid w-full h-auto grid-cols-2 p-1 bg-muted">
            <TabsTrigger
              value="cajas"
              className="flex items-center gap-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <Package className={`h-4 w-4 ${vista === 'cajas' ? kpiColor.icon : 'text-muted-foreground'}`} />
              <span className="text-sm font-medium">Unidades vendidas (cajas)</span>
            </TabsTrigger>
            <TabsTrigger
              value="dinero"
              className="flex items-center gap-2 py-2.5 data-[state=active]:bg-background data-[state=active]:shadow-sm"
            >
              <span className="text-sm font-medium">Total vendido ({simboloMoneda})</span>
            </TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {/* KPI consolidado: total facturas + sub-stats */}
      <div className="grid gap-4 md:grid-cols-2">
        <StatCardConsolidado
          icon={FileText}
          title={`Total ${terminoComprobante(configId, { plural: true })}`}
          value={productoSel ? validadasProducto : validadas}
          subStats={productoSel || derivadoSinProducto ? [] : [
            { label: 'Duplicados', value: duplicadas, tone: 'danger' },
          ]}
          colorNombre={color.nombre}
          colorTono={color.tono}
          configId={configId}
          isLoading={productoSel ? ventasPorTipo.isLoading : derivadoSinProducto ? cargandoAgregado : resumen.isLoading}
        />
        <KpiCard
          icon={Package}
          label={ventasCardEsValor ? 'Ventas Totales' : 'Unidades Vendidas en Caja'}
          value={(derivadoSinProducto ? cargandoAgregado : porProductoTotal.isLoading) ? '—' : formatVolumenCard(totalVolumen)}
          hint={ventasCardEsValor ? 'Monto vendido en el periodo' : 'Cajas equivalentes normalizadas'}
          colorNombre={color.nombre}
          colorTono={color.tono}
          gradient
        />
      </div>

      {/* Top productos | agrupación con tabs | mapa de provincias */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <BarChartLabelLeft
          title={esValor ? `Top productos (ventas ${simboloMoneda})` : 'Top productos'}
          data={productoData}
          colorNombre={color.nombre}
          isLoading={agregadosProducto ? cargandoAgregado : porProductoTotal.isLoading}
          icon={Package}
          formatValue={formatVolumen}
          maxVisible={6}
          headerStat={{ value: totalProductos, label: 'únicos' }}
          configId={configId}
        />
        <BarChartAgrupado
          title={esValor ? `Ventas ${simboloMoneda} por agrupación` : 'Cajas vendidas por agrupación'}
          colorNombre={color.nombre}
          icon={Layers}
          formatValue={formatVolumen}
          options={(
            [
              // Medifarma Perú (id 7): la agrupación es Representante y
              // Departamento (el upstream no expone datos de cadena).
              ...(configId === 7
                ? []
                : [
                    {
                      key: 'cadena',
                      label: campoCadena?.label ?? 'Cadena',
                      icon: Store,
                      data: cadenaData,
                      isLoading: agregadosProducto ? cargandoAgregado : porCadena.isLoading,
                      error: agregadosProducto ? null : porCadena.error,
                      headerStat: { value: totalCadenas, label: 'únicas' },
                    },
                  ]),
              {
                key: 'representante',
                label: campoRepresentante?.label ?? 'Representante',
                icon: UserCheck,
                data: repData,
                isLoading: agregadosProducto ? cargandoAgregado : porRep.isLoading,
                error: agregadosProducto ? null : porRep.error,
                headerStat: { value: totalRepresentantes, label: 'únicos' },
              },
              {
                key: 'provincia',
                label: regionTerminoCap(configId),
                icon: MapIcon,
                data: provinciaData,
                isLoading: agregadosProducto ? cargandoAgregado : porProvincia.isLoading,
                error: agregadosProducto ? null : porProvincia.error,
                headerStat: { value: totalProvincias, label: 'únicas' },
              },
            ] satisfies DatasetOpcion[]
          )}
          configId={configId}
        />
        <MapaRegiones
          title={`Ventas por ${regionTermino(configId)}`}
          data={provinciaData}
          colorNombre={color.nombre}
          isLoading={agregadosProducto ? cargandoAgregado : porProvincia.isLoading}
          error={agregadosProducto ? null : porProvincia.error}
          icon={MapIcon}
          headerStat={{ value: totalProvincias, label: 'únicas' }}
          geojsonUrl={GEOJSON_PAIS(configId)}
          insetName={insetPais(configId)}
          configId={configId}
        />
      </div>

      {/* Línea del volumen de ventas por fecha ($ o cajas según modo), con
          segunda línea gris del nº de facturas validadas por periodo. El toggle
          alterna entre día a día (periodo seleccionado) y semana/mes (todo el
          histórico). */}
      <AreaChartFecha
        title={`${esValor ? `Ventas ${simboloMoneda}` : 'Cajas vendidas'} — ${GRAN_RITMO[granSerie]}`}
        data={fechaData}
        colorNombre={color.nombre}
        colorTono={color.tono}
        isLoading={agregadosProducto ? cargandoAgregado : serie.isLoading || serieFacturas.isLoading}
        icon={Layers}
        formatValue={formatVolumen}
        primaryLabel={esValor ? `Ventas ${simboloMoneda}` : 'Ventas cajas'}
        secondaryLabel={`Cantidad de ${terminoComprobante(configId, { plural: true })}`}
        secondaryFormatValue={(v) => formatDecimal(v, configId, 0)}
        headerActions={<GranularidadToggle value={granSerie} onChange={setGranSerie} />}
        granularidad={granSerie}
        stats={[
          {
            label: esValor
              ? `Ventas ${simboloMoneda}/${GRAN_SUFIJO[granSerie]}`
              : `Cajas/${GRAN_SUFIJO[granSerie]}`,
            value:
              (agregadosProducto ? cargandoAgregado : serie.isLoading || serieFacturas.isLoading)
                ? '—'
                : formatVolumen(promedioDiario.ventas),
            series: 'primary',
          },
          {
            label: `${terminoComprobante(configId, { plural: true, capitalize: true })}/${GRAN_SUFIJO[granSerie]}`,
            value:
              (agregadosProducto ? cargandoAgregado : serie.isLoading || serieFacturas.isLoading)
                ? '—'
                : formatDecimal(promedioDiario.facturas, configId, 1),
            series: 'secondary',
          },
        ]}
        configId={configId}
      />

      {/* Tabla de facturas con formato medifarma-fidelizacion.
          La barra de filtros se renderiza arriba a nivel de página.
          `solo_validadas` filtra los 3 estados válidos (aprobada/confirmada/
          corregida) en el SERVIDOR, así la paginación queda pareja (25/pág)
          en vez de filtrar client-side sobre cada página. */}
      <div className="space-y-4">
        <TablaFacturasFidelizacion
          configId={configId}
          titulo={terminoComprobante(configId, { plural: true, capitalize: true })}
          filtros={filtrosTabla}
          // Con producto seleccionado la tabla se alimenta client-side con los
          // comprobantes que incluyen ese producto (el upstream no lo soporta).
          datosOverride={datosTablaProducto}
          overrideLoading={cargandoAgregado}
          onComprobanteClick={(id) => {
            setFacturaId(id)
            setFacturaOpen(true)
          }}
        />
      </div>

      <FacturaPreviewDialog
        configId={configId}
        facturaId={facturaId}
        open={facturaOpen}
        onOpenChange={setFacturaOpen}
      />
    </div>
  )
}
